/*       */

import Immutable from 'app/utils/immutable/Immutable';
import { get } from 'app/utils/lo/lo';
import history from 'store/History';
import { fetchThreadsList, fetchThreadMessages, postChatMessage, roomFileUpload, postThreadFollow } from 'app/utils/chat/httpChatApi';
import { normalizeMessage, normalizeThreadMessage, buildAliveLink, getSubscriptionByRid, getCredentials } from 'app/utils/chat/chatUtils';
import { getChatSocket } from 'store/actions/chat/chatActions';
import { closeSidebar } from 'store/actions/sidebar/sidebarActions';

export const OPEN_THREAD_VIEW = '@@affectli/chat/thread/OPEN_THREAD_VIEW';
export const OPEN_THREADS_LIST = '@@affectli/chat/thread/OPEN_THREADS_LIST';
export const CLOSE_THREAD_PANEL = '@@affectli/chat/thread/CLOSE_THREAD_PANEL';
export const SET_THREADS_FILTER = '@@affectli/chat/thread/SET_THREADS_FILTER';
export const RESET_THREAD_STATE = '@@affectli/chat/thread/RESET_THREAD_STATE';
export const LOAD_THREAD_MESSAGES_STARTED = '@@affectli/chat/thread/LOAD_THREAD_MESSAGES_STARTED';
export const LOAD_THREAD_MESSAGES = '@@affectli/chat/thread/LOAD_THREAD_MESSAGES';
export const LOAD_THREADS_LIST_STARTED = '@@affectli/chat/thread/LOAD_THREADS_LIST_STARTED';
export const LOAD_THREADS_LIST = '@@affectli/chat/thread/LOAD_THREADS_LIST';
export const ADD_THREAD_MESSAGE = '@@affectli/chat/thread/ADD_THREAD_MESSAGE';
export const EDIT_THREAD_MESSAGE = '@@affectli/chat/thread/EDIT_THREAD_MESSAGE';
export const CLEAR_SUBSCRIPTION_TUNREAD = '@@affectli/chat/thread/CLEAR_SUBSCRIPTION_TUNREAD';
export const MERGE_SUBSCRIPTION_TUNREAD = '@@affectli/chat/thread/MERGE_SUBSCRIPTION_TUNREAD';
export const TOGGLE_THREAD_FOLLOW = '@@affectli/chat/thread/TOGGLE_THREAD_FOLLOW';
export const SET_THREADS_SEARCH = '@@affectli/chat/thread/SET_THREADS_SEARCH';

export const openThreadView = (rid, parentMessage) => (dispatch, getState) => {
    dispatch(closeSidebar()); // mutually exclusive with the global right sidebar
    dispatch({ type: OPEN_THREAD_VIEW, payload: Immutable({ rid, parentMessage }) });
    const socket = getChatSocket();
    if (parentMessage.tcount) {
        if (socket) {
            socket.readThreads(parentMessage.id).catch(() => {}); // non-fatal: server-side read marker
        }
        dispatch({ type: CLEAR_SUBSCRIPTION_TUNREAD, payload: Immutable({ rid, tmid: parentMessage.id }) });
        dispatch(loadThreadMessages(parentMessage.id));
    }
};

export const openThreadsList = rid => dispatch => {
    dispatch(closeSidebar()); // mutually exclusive with the global right sidebar
    dispatch({ type: OPEN_THREADS_LIST, payload: Immutable({ rid }) });
    dispatch(loadThreadsList(rid));
};

export const closeThreadPanel = () => ({ type: CLOSE_THREAD_PANEL });

export const resetThreadState = () => ({ type: RESET_THREAD_STATE });

export const setThreadsFilter = (rid, filter) => dispatch => {
    dispatch({ type: SET_THREADS_FILTER, payload: filter });
    dispatch(loadThreadsList(rid, filter));
};

export const loadThreadsList = (rid, filterOverride, textOverride) => async (dispatch, getState) => {
    const state = getState();
    const filter = filterOverride || get(state, 'chat.thread.filter') || 'all';
    const text = textOverride != null ? textOverride : (get(state, 'chat.thread.searchText') || '');
    dispatch({ type: LOAD_THREADS_LIST_STARTED });
    try {
        const threads = await fetchThreadsList({ roomId: rid, type: filter, text }, getCredentials(state));
        dispatch({ type: LOAD_THREADS_LIST, payload: Immutable({ threads: threads.map(normalizeThreadMessage) }) });
    } catch (e) {
        dispatch({ type: LOAD_THREADS_LIST, error: true, meta: Immutable({ errorMessage: e.message || 'An error occurred loading threads.' }) });
    }
};

// Server-side thread search (RC chat.getThreadsList `text` param), combined with the
// active type filter — mirrors Rocket.Chat.
export const setThreadsSearch = (rid, text) => dispatch => {
    dispatch({ type: SET_THREADS_SEARCH, payload: text });
    dispatch(loadThreadsList(rid, undefined, text));
};

// Follow/unfollow a thread. Following state lives in the thread's replies[] (RC
// followers); optimistically patch it, call RC, roll back on failure.
export const toggleThreadFollow = (tmid, follow) => async (dispatch, getState) => {
    const state = getState();
    const userId = get(state, 'user.chat.id');
    dispatch({ type: TOGGLE_THREAD_FOLLOW, payload: Immutable({ tmid, userId, follow }) });
    try {
        await postThreadFollow({ tmid, follow }, getCredentials(state));
        // re-apply the same toggle (patchReplies is idempotent) carrying a success toast,
        // so the toaster only shows once the server actually confirms the follow change.
        dispatch({
            type: TOGGLE_THREAD_FOLLOW,
            payload: Immutable({ tmid, userId, follow }),
            meta: Immutable({ successMessage: follow ? 'You followed this message.' : 'You unfollowed this message.' }),
        });
    } catch (e) {
        // revert the optimistic toggle + surface the error toast
        dispatch({
            type: TOGGLE_THREAD_FOLLOW,
            error: true,
            payload: Immutable({ tmid, userId, follow: !follow }),
            meta: Immutable({ errorMessage: e.message || `An error occurred ${follow ? 'following' : 'unfollowing'} the thread.` }),
        });
    }
};

export const loadThreadMessages = tmid => async (dispatch, getState) => {
    dispatch({ type: LOAD_THREAD_MESSAGES_STARTED });
    try {
        const messages = await fetchThreadMessages({ tmid }, getCredentials(getState()));
        dispatch({ type: LOAD_THREAD_MESSAGES, payload: Immutable({ tmid, messages: messages.map(normalizeThreadMessage) }) });
    } catch (e) {
        dispatch({ type: LOAD_THREAD_MESSAGES, error: true, meta: Immutable({ errorMessage: e.message || 'An error occurred loading thread messages.' }) });
    }
};

// REST path for ALL room types — deliberately NOT the sendChatMessage action
// (its SEND_CHAT_MESSAGE dispatch inserts into the main room list).
export const sendThreadMessage = (rid, tmid, msg) => async (dispatch, getState) => {
    try {
        const resMessage = await postChatMessage({ message: { rid, msg, tmid } }, getCredentials(getState()));
        if (resMessage) {
            dispatch({ type: ADD_THREAD_MESSAGE, payload: Immutable({ message: normalizeMessage(resMessage) }) });
        }
        return resMessage;
    } catch (e) {
        dispatch({ type: ADD_THREAD_MESSAGE, error: true, meta: Immutable({ errorMessage: e.message || 'An error occurred sending the thread reply.' }) });
        return null;
    }
};

export const uploadThreadFile = (rid, tmid, file, filename, description) => async (dispatch, getState) => {
    try {
        const resMessage = await roomFileUpload({ file, id: rid, filename, description, tmid }, getCredentials(getState()));
        if (resMessage) {
            dispatch({ type: ADD_THREAD_MESSAGE, payload: Immutable({ message: normalizeMessage(resMessage) }) });
        }
        return resMessage;
    } catch (e) {
        dispatch({ type: ADD_THREAD_MESSAGE, error: true, meta: Immutable({ errorMessage: e.message || 'An error occurred uploading the attachment.' }) });
        return null;
    }
};

// ChatItem (entity-sidebar) surface: pre-seed thread state, then navigate to A-Live.
export const openThreadInAlive = (rid, message) => (dispatch, getState) => {
    dispatch(openThreadView(rid, message));
    const subs = get(getState(), 'chat.subscriptions.data') || [];
    const sub = getSubscriptionByRid(subs, rid) || {};
    const { t: subType, rel, customFields } = sub;
    if (rel && rel.id) {
        history.push(buildAliveLink(subType, rel, customFields));
    }
};
